import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Table, Form, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faBroom, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import ActionIcon from 'src/components/general/ActionIcon';
import AppLoading from 'src/components/common/AppLoading';
import { isNotEmptyArray } from 'src/services/validationService';
import { clearTreasuryAuditList, clearTreasuryAuditDetail } from 'src/redux/audit/auditActions';
import {
    getTreasuryAuditListData,
    getTreasuryAuditListIsFetching,
    getTreasuryAuditDetailData,
    getTreasuryAuditDetailIsFetching,
} from 'src/redux/audit/auditReducer';
import { tryGetTreasuryAuditList, tryGetTreasuryAuditById } from 'src/redux/audit/auditActionCreator';
import RCPagination from 'src/components/common/RCPagination';
import TreasuryAuditDetailModal from './TreasuryAuditDetailModal';
import { AdministrativeServiceData, administrativeService } from 'src/utils/administrativeServiceUtils.js';

const ACCIONES = [
    { value: 'POST',   label: 'Enviado' },
    { value: 'DELETE', label: 'Borrado' },
];

const STATUS_OPTIONS = [
    { value: '200', label: 'OK' },
    { value: '400', label: 'No se completó' },
];

const AuditsTreasuryPage = () => {
    const dispatch = useDispatch();
    const { handleSubmit, register, reset } = useForm();

    const administrativeServiceData = AdministrativeServiceData();

    const listData = useSelector(state => getTreasuryAuditListData(state));
    const isFetching = useSelector(state => getTreasuryAuditListIsFetching(state));
    const detailData = useSelector(state => getTreasuryAuditDetailData(state));
    const detailIsFetching = useSelector(state => getTreasuryAuditDetailIsFetching(state));

    const records = listData?.records ?? [];
    const hasRecords = isNotEmptyArray(records);

    // La API devuelve metadata: { page, pageSize, total, filteredCount }
    const totalItems = listData?.metadata?.total ?? 0;
    const currentPage = (listData?.metadata?.page ?? 0) + 1; // convertir de 0-indexed a 1-indexed
    const pageSize = listData?.metadata?.pageSize ?? 10;

    const defaultParams = { page: 0, size: 10 };
    const [filters, setFilters] = useState({});

    // Modal
    const [showModal, setShowModal] = useState(false);

    const handleOpenDetail = (id) => {
        dispatch(clearTreasuryAuditDetail());
        dispatch(tryGetTreasuryAuditById(id));
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        dispatch(clearTreasuryAuditDetail());
    };

    const loadData = (params) => {
        dispatch(clearTreasuryAuditList());
        dispatch(tryGetTreasuryAuditList(params));
    };

    const handleSearch = (formData) => {
        const cleaned = Object.fromEntries(
            Object.entries(formData).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        );
        setFilters(cleaned);
        loadData({ ...defaultParams, ...cleaned });
    };

    const clearFilters = () => {
        setFilters({});
        reset();
        loadData(defaultParams);
    };

    const onPageChange = (pageNumber) => {
        loadData({ ...defaultParams, ...filters, page: pageNumber - 1 }); // convert back to 0-indexed
    };

    useEffect(() => {
        loadData(defaultParams);
        return () => {
            dispatch(clearTreasuryAuditList());
        };
    }, []);

    const ACCION_LABELS = {
        'POST':   'Enviado',
        'DELETE': 'Borrado',
    };

    const statusBadge = (statusCode) => {
        if (!statusCode) return <Badge variant='secondary'>—</Badge>;
        const isError = statusCode >= 400;
        return <Badge variant={isError ? 'danger' : 'success'}>{isError ? 'No se completó' : 'OK'}</Badge>;
    };

    const accionBadge = (accion) => {
        if (!accion) return <Badge variant='secondary'>—</Badge>;
        const label = ACCION_LABELS[accion] ?? accion;
        return <Badge variant={accion === 'DELETE' ? 'danger' : 'primary'}>{label}</Badge>;
    };

    return (
        <>
            <Container fluid>
                <Card className='my-3'>
                    <Card.Header className='h6'>
                        Auditoría - Tesorería General
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleSubmit(handleSearch)} autoComplete='off'>
                            <Table striped hover size='sm'>
                                <thead>
                                    <tr>
                                        <th className='text-center align-middle' width='10%'>Fecha</th>
                                        <th className='text-center align-middle' width='10%'>Hora</th>
                                        <th className='text-center align-middle' width='10%'>Acción</th>
                                        <th className='text-center align-middle' width='10%'>Estado</th>
                                        <th className='text-center align-middle' width='10%'>Pedido de Fondos</th>
                                        <th className='text-center align-middle' width='40%'>Serv. Administrativo</th>
                                        <th className='text-center align-middle' width='10%'>Detalle</th>
                                    </tr>
                                    {/* Filters row */}
                                    <tr className='secondary'>
                                        <th className='text-center align-middle'>
                                            <Form.Control
                                                type='date'
                                                name='createdDate'
                                                ref={register}
                                                size='sm'
                                            />
                                        </th>
                                        <th></th>
                                        <th className='text-center align-middle'>
                                            <Form.Control as='select' name='accion' ref={register} size='sm'>
                                                <option value=''>Todos</option>
                                                {ACCIONES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                            </Form.Control>
                                        </th>
                                        <th className='text-center align-middle'>
                                            <Form.Control as='select' name='statusCode' ref={register} size='sm'>
                                                <option value=''>Todos</option>
                                                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                            </Form.Control>
                                        </th>
                                        <th className='text-center align-middle'>
                                            <Form.Control
                                                type='text'
                                                name='pf'
                                                placeholder='Ej: 113/26'
                                                ref={register}
                                                size='sm'
                                            />
                                        </th>
                                        <th className='text-center align-middle'>
                                            <Form.Control as='select' name='administrativeServiceId' ref={register} size='sm'>
                                                <option value=''>Todos</option>
                                                {Array.isArray(administrativeServiceData) && administrativeServiceData.map(item => (
                                                    <option key={item.id} value={item.id}>{item.code} - {item.shortName}</option>
                                                ))}
                                            </Form.Control>
                                        </th>
                                        <th className='text-center align-middle'>
                                            <div className='d-flex justify-content-around'>
                                                <ActionIcon
                                                    size='lg'
                                                    id='search-treasury'
                                                    className='btn-primary search-button text-white-color px-4 py-1 mx-2'
                                                    toolTipText='Filtrar'
                                                    icon={faFilter}
                                                    type='submit'
                                                    onSubmit={handleSearch}
                                                />
                                                <ActionIcon
                                                    size='lg'
                                                    id='clear-treasury'
                                                    className='btn-primary clean-filter text-white-color px-4 py-1 mx-2'
                                                    toolTipText='Limpiar filtros'
                                                    icon={faBroom}
                                                    type='reset'
                                                    onClick={clearFilters}
                                                />
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isFetching && (
                                        <tr>
                                            <td colSpan='7' className='text-center'>
                                                <AppLoading isLoading />
                                            </td>
                                        </tr>
                                    )}
                                    {!isFetching && !hasRecords && (
                                        <tr>
                                            <td colSpan='7'>
                                                <Alert variant='info' className='mb-0 text-center'>
                                                    <FontAwesomeIcon icon={faFilter} className='mr-2' />
                                                    No se encontraron registros.
                                                </Alert>
                                            </td>
                                        </tr>
                                    )}
                                    {!isFetching && hasRecords && records.map((item) => {
                                        const [datePart, timePart] = (item?.createdDate ?? '').split(' ');
                                        return (
                                            <tr key={item?.id} >
                                                <td className='text-center align-middle'>{datePart || '—'}</td>
                                                <td className='text-center align-middle'>{timePart || '—'}</td>
                                                <td className='text-center align-middle'>{accionBadge(item?.accion)}</td>
                                                <td className='text-center align-middle'>{statusBadge(item?.statusCode)}</td>
                                                <td className='text-center align-middle'>{item?.pf ?? '—'}</td>
                                                <td className='text-start align-middle'>{administrativeService(item?.administrativeServiceId, administrativeServiceData ?? []) || '—'}</td>
                                                <td className='text-center align-middle p-0 m-0'>
                                                    <ActionIcon size='lg' toolTipText='Detalle del log' icon={faSearch} onClick={() => handleOpenDetail(item?.id)} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Form>

                        {hasRecords && (
                            <RCPagination
                                activePage={currentPage}
                                itemsCountPerPage={pageSize}
                                totalItemsCount={totalItems}
                                pageRangeDisplayed={7}
                                thePage={currentPage}
                                totalItems={totalItems}
                                innerClass="justify-content-center"
                                onChange={onPageChange}
                            />
                        )}
                    </Card.Body>
                </Card>
            </Container>

            <TreasuryAuditDetailModal
                show={showModal}
                handleClose={handleCloseModal}
                detailData={detailData}
                isFetching={detailIsFetching}
            />
        </>
    );
};

export default AuditsTreasuryPage;
